import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { getAuthHeaders } from '../lib/authHeaders';
import {
  ArrowLeft, Lock, ChevronRight, Star, Trophy, Sparkles,
  Target, BookOpen, Compass, Award, Play, CheckCircle
} from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { cn } from '../lib/utils';
import SEO, { PageSEO } from '../components/SEO';

import { API } from '../config/api';

// getAuthHeaders imported from shared utility

// Individual Challenge Card - Orb-style for SquidWorld
const ChallengeCard = ({ challenge, index, isOrbStyle = false }) => {
  const isLocked = challenge.is_locked && challenge.order > 1;
  const isCompleted = challenge.is_completed;
  
  // Orb-style for SquidWorld (no rectangles/squares)
  if (isOrbStyle) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: index * 0.03, type: 'spring', stiffness: 200 }}
        className="flex flex-col items-center"
      >
        <Link 
          to={isLocked ? '#' : `/challenge/${challenge.challenge_id || challenge.id}`}
          className={cn(
            'relative w-20 h-20 md:w-24 md:h-24 rounded-full flex items-center justify-center transition-all duration-300',
            'border-2 shadow-lg',
            isCompleted && 'bg-gradient-to-br from-green-500/30 to-emerald-600/30 border-green-500 shadow-green-500/30',
            isLocked && 'bg-slate-800/50 border-slate-700 opacity-50 cursor-not-allowed',
            !isCompleted && !isLocked && 'bg-gradient-to-br from-cyan-500/20 to-blue-600/20 border-cyan-500/50 hover:border-cyan-400 hover:shadow-cyan-500/30 hover:scale-110 cursor-pointer'
          )}
        >
          {/* Glow ring */}
          {!isLocked && !isCompleted && (
            <div className="absolute inset-0 rounded-full bg-cyan-500/20 animate-pulse blur-md" />
          )}
          
          {/* Inner content */}
          <div className="relative z-10 text-center">
            {isCompleted ? (
              <CheckCircle className="w-8 h-8 text-green-400" />
            ) : isLocked ? (
              <Lock className="w-6 h-6 text-slate-500" />
            ) : (
              <span className="text-2xl">{challenge.icon || '🎯'}</span>
            )}
          </div>
          
          {/* Order badge */}
          <div className={cn(
            'absolute -top-1 -right-1 w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center',
            isCompleted ? 'bg-green-500 text-white' : 'bg-slate-700 text-slate-300'
          )}>
            {challenge.order}
          </div>
        </Link>
        
        {/* Title below orb */}
        <p className={cn(
          'mt-2 text-xs text-center max-w-[100px] line-clamp-2',
          isCompleted ? 'text-green-400' : isLocked ? 'text-slate-500' : 'text-slate-300'
        )}>
          {challenge.name}
        </p>
        
        {/* XP badge */}
        {!isLocked && (
          <Badge variant="outline" className="mt-1 text-[9px] px-1.5 py-0 h-4 border-cyan-500/30 text-cyan-400">
            +{challenge.xp} XP
          </Badge>
        )}
      </motion.div>
    );
  }
  
  // Default card style (for other worlds)
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: index * 0.02 }}
    >
      <Card className={cn(
        'relative border-2 transition-all duration-300 hover:scale-105 cursor-pointer overflow-hidden',
        isCompleted && 'border-green-500/50 bg-green-900/20',
        isLocked && 'opacity-50 border-slate-700',
        !isCompleted && !isLocked && 'border-slate-700 hover:border-cyan-500/50 bg-slate-800/50'
      )}>
        <CardContent className="p-3">
          <div className="flex items-start gap-2">
            <div className={cn(
              'w-10 h-10 rounded-lg flex items-center justify-center text-xl shrink-0',
              isCompleted ? 'bg-green-500/20' : isLocked ? 'bg-slate-700' : 'bg-cyan-500/20'
            )}>
              {isCompleted ? <CheckCircle className="w-5 h-5 text-green-400" /> : challenge.icon}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-1 mb-0.5">
                <span className="text-[10px] text-slate-500">#{challenge.order}</span>
                {isLocked ? (
                  <Lock className="w-3 h-3 text-slate-500" />
                ) : (
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 border-cyan-500/30 text-cyan-400">
                    +{challenge.xp} XP
                  </Badge>
                )}
              </div>
              <h4 className="text-xs font-semibold text-white line-clamp-1">{challenge.name}</h4>
              <p className="text-[10px] text-slate-400 line-clamp-1">{challenge.description}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

// Challenge Category Card
const CategoryCard = ({ category, index }) => {
  const isLocked = category.is_locked;
  const progress = category.completed_count / category.total_count * 100 || 0;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
    >
      <Card className={cn(
        'group relative overflow-hidden transition-all duration-300',
        'border-2',
        isLocked ? 'opacity-50 cursor-not-allowed border-slate-700 bg-slate-800/50' :
        progress >= 100 ? 'border-green-500 bg-gradient-to-br from-green-900/20 to-slate-900' :
        'border-slate-600 hover:border-cyan-500/50 bg-slate-800/50 hover:bg-slate-800'
      )}>
        <CardContent className="p-5">
          <div className="flex items-start justify-between mb-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500/20 to-blue-500/20 flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-cyan-400" />
            </div>
            {isLocked ? (
              <Lock className="w-5 h-5 text-slate-500" />
            ) : progress >= 100 ? (
              <CheckCircle className="w-5 h-5 text-green-400" />
            ) : (
              <Badge variant="outline" className="text-xs border-cyan-500/30 text-cyan-400">
                {category.completed_count}/{category.total_count}
              </Badge>
            )}
          </div>
          
          <h3 className="text-lg font-bold text-white mb-1">{category.name}</h3>
          <p className="text-sm text-slate-400 mb-3 line-clamp-2">{category.description}</p>
          
          {!isLocked && (
            <div className="mb-3">
              <Progress value={progress} className="h-1.5" />
            </div>
          )}
          
          <Button 
            className={cn(
              'w-full text-sm',
              isLocked ? 'bg-slate-700 cursor-not-allowed' :
              'bg-cyan-600 hover:bg-cyan-500'
            )}
            disabled={isLocked}
            size="sm"
          >
            {isLocked ? (
              <>
                <Lock className="w-3 h-3 mr-2" />
                Locked
              </>
            ) : progress >= 100 ? (
              <>
                <Trophy className="w-3 h-3 mr-2" />
                Review
              </>
            ) : (
              <>
                <Play className="w-3 h-3 mr-2" />
                Start Learning
              </>
            )}
          </Button>
        </CardContent>
      </Card>
    </motion.div>
  );
};

// Coming Soon State
const ComingSoonState = ({ world }) => (
  <div className="text-center py-20">
    <motion.div
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div className="text-8xl mb-6">{world.icon}</div>
      <h2 className="text-3xl font-bold text-white mb-4">Coming Soon</h2>
      <p className="text-lg text-slate-400 max-w-xl mx-auto mb-8">
        {world.coming_soon_message || `${world.name} challenges are being prepared. Stay tuned!`}
      </p>
      
      {/* Focus Areas Preview */}
      <div className="max-w-2xl mx-auto">
        <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">
          What You&apos;ll Learn
        </h3>
        <div className="flex flex-wrap justify-center gap-2">
          {world.focus_areas?.map((area, idx) => (
            <Badge key={idx} className="bg-slate-800 text-slate-300 border-slate-700">
              {area}
            </Badge>
          ))}
        </div>
      </div>
      
      {/* Notification signup */}
      <Card className="max-w-md mx-auto mt-8 bg-slate-800/50 border-slate-700">
        <CardContent className="p-6 text-center">
          <Sparkles className="w-8 h-8 text-cyan-400 mx-auto mb-3" />
          <p className="text-sm text-slate-300 mb-4">
            Be the first to know when {world.name} launches
          </p>
          <Button className="bg-cyan-600 hover:bg-cyan-500">
            Notify Me
          </Button>
        </CardContent>
      </Card>
    </motion.div>
  </div>
);

const WorldDetail = ({ embedded = false, worldIdOverride = null }) => {
  const params = useParams();
  const worldId = worldIdOverride || params.worldId;
  const navigate = useNavigate();
  const [world, setWorld] = useState(null);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (worldId) {
      const fetchWorldData = async () => {
        try {
          const res = await axios.get(`${API}/worlds/${worldId}`, { headers: await getAuthHeaders() });
          setWorld(res.data.world);
          setCategories(res.data.categories || []);
        } catch (error) {
          console.error('Error fetching world data:', error);
        } finally {
          setIsLoading(false);
        }
      };
      fetchWorldData();
    }
  }, [worldId]);

  if (isLoading) {
    return (
      <div className={cn("min-h-screen bg-gradient-to-b from-slate-900 via-blue-950/30 to-slate-900", embedded && "min-h-0")}>
      <SEO {...PageSEO.worlds} url="https://squidemy.com/worlds" />
      
        {!embedded && <Navigation />}
        <div className={cn("flex items-center justify-center", embedded ? "py-20" : "py-40")}>
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-400" />
        </div>
      </div>
    );
  }

  if (!world) {
    return (
      <div className={cn("min-h-screen bg-gradient-to-b from-slate-900 via-blue-950/30 to-slate-900", embedded && "min-h-0")}>
        {!embedded && <Navigation />}
        <div className={cn("flex flex-col items-center justify-center", embedded ? "py-20" : "py-40")}>
          <h2 className="text-2xl font-bold text-white mb-4">World Not Found</h2>
          <Button onClick={() => navigate('/worlds')}>Return to Worlds</Button>
        </div>
      </div>
    );
  }

  // Get gradient based on world
  const getGradient = () => {
    switch (world.id) {
      case 'squidland': return 'from-cyan-900 via-blue-950 to-slate-900';
      case 'squidworld': return 'from-green-900 via-emerald-950 to-slate-900';
      default: return 'from-slate-900 via-blue-950/30 to-slate-900';
    }
  };

  const hasContent = categories.length > 0 || world.has_challenges;
  const totalProgress = world.completion_percentage || 0;

  return (
    <div className={cn('min-h-screen bg-gradient-to-b', getGradient(), embedded && 'min-h-0')}>
      {!embedded && <Navigation />}
      
      <section className={cn("relative pb-12 overflow-hidden", embedded ? "pt-8" : "pt-36")}>
        {/* Animated background */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-cyan-500/10 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse" style={{animationDelay: '2s'}} />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          {/* Back Button - Only show when not embedded */}
          {!embedded && (
            <Link to="/worlds" className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-6 transition-colors">
              <ArrowLeft className="w-4 h-4" />
              Back to Worlds
            </Link>
          )}
          
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-start gap-6 mb-8">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="text-7xl"
            >
              {world.icon}
            </motion.div>
            
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-3 mb-2">
                {world.tier !== null && world.tier !== undefined && (
                  <Badge className="bg-slate-700/80 text-white">
                    Tier {world.tier}
                  </Badge>
                )}
                <Badge className={cn(
                  'border',
                  world.difficulty === 'Beginner' ? 'bg-green-500/20 text-green-300 border-green-500/30' :
                  world.difficulty === 'Intermediate' ? 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30' :
                  world.difficulty === 'Advanced' ? 'bg-orange-500/20 text-orange-300 border-orange-500/30' :
                  'bg-red-500/20 text-red-300 border-red-500/30'
                )}>
                  {world.difficulty}
                </Badge>
                {world.world_type === 'flat' && (
                  <Badge className="bg-blue-500/20 text-blue-300 border-blue-500/30">
                    🌍 Flat World
                  </Badge>
                )}
              </div>
              
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-2">{world.name}</h1>
              <p className="text-lg text-cyan-300 mb-4">{world.subtitle}</p>
              <p className="text-white/70 max-w-2xl">{world.description}</p>
            </div>
          </div>

          {/* Progress Bar (if has content) */}
          {hasContent && (
            <Card className="bg-slate-900/50 border-slate-700 mb-8">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-400">Overall Progress</span>
                  <span className="text-sm font-semibold text-white">{Math.round(totalProgress)}%</span>
                </div>
                <Progress value={totalProgress} className="h-2" />
              </CardContent>
            </Card>
          )}

          {/* Lore Card */}
          <Card className="bg-slate-900/50 border-slate-700 mb-8">
            <CardContent className="p-6">
              <p className="text-white/80 italic leading-relaxed">&ldquo;{world.lore}&rdquo;</p>
            </CardContent>
          </Card>

          {/* Certifications */}
          {world.cert_mappings?.length > 0 && (
            <div className="mb-8">
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                <Award className="w-4 h-4" />
                Certification Paths
              </h3>
              <div className="flex flex-wrap gap-2">
                {world.cert_mappings.map((cert, idx) => (
                  <Badge key={idx} className="bg-yellow-500/20 text-yellow-300 border-yellow-500/30">
                    {cert}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Content Section */}
      <section className="py-8 px-4">
        <div className="max-w-7xl mx-auto">
          {world.challenges && world.challenges.length > 0 ? (
            <>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                  <Target className="w-6 h-6 text-cyan-400" />
                  {world.total_challenges || world.challenges.length} Challenges
                </h2>
                <Badge className="bg-cyan-500/20 text-cyan-300 border-cyan-500/30">
                  {world.completed_count || 0}/{world.challenges.length} Completed
                </Badge>
              </div>
              
              {/* Group challenges by category */}
              {(() => {
                const groupedChallenges = world.challenges.reduce((acc, challenge) => {
                  const cat = challenge.category || 'General';
                  if (!acc[cat]) acc[cat] = [];
                  acc[cat].push(challenge);
                  return acc;
                }, {});
                
                const isSquidWorld = world.id === 'squidworld';
                
                return Object.entries(groupedChallenges).map(([category, challenges]) => (
                  <div key={category} className="mb-12">
                    <h3 className="text-lg font-semibold text-slate-300 mb-6 flex items-center gap-2">
                      <span className="text-xl">{challenges[0]?.icon || '📚'}</span>
                      {category}
                      <Badge variant="outline" className="ml-2">{challenges.length} challenges</Badge>
                    </h3>
                    
                    {isSquidWorld ? (
                      /* Globe/Orb-centric layout for SquidWorld - No squares/rectangles */
                      <div className="relative">
                        {/* Central Hub */}
                        <div className="flex justify-center mb-8">
                          <div className="w-24 h-24 rounded-full bg-gradient-to-br from-emerald-500/30 to-cyan-600/30 border-2 border-emerald-500 flex items-center justify-center shadow-lg shadow-emerald-500/30">
                            <span className="text-3xl">{challenges[0]?.icon || '🌍'}</span>
                          </div>
                        </div>
                        
                        {/* Challenges in orbital rings */}
                        <div className="flex flex-wrap justify-center gap-6 md:gap-8">
                          {challenges.map((challenge, index) => (
                            <ChallengeCard 
                              key={challenge.challenge_id || challenge.id} 
                              challenge={challenge} 
                              index={index}
                              isOrbStyle={true}
                            />
                          ))}
                        </div>
                      </div>
                    ) : (
                      /* Standard grid for other worlds */
                      <div className="grid md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                        {challenges.map((challenge, index) => (
                          <ChallengeCard key={challenge.challenge_id || challenge.id} challenge={challenge} index={index} />
                        ))}
                      </div>
                    )}
                  </div>
                ));
              })()}
            </>
          ) : categories.length > 0 ? (
            <>
              <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
                <Target className="w-6 h-6 text-cyan-400" />
                Learning Paths
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {categories.map((category, index) => (
                  <CategoryCard key={category.id} category={category} index={index} />
                ))}
              </div>
            </>
          ) : (
            <ComingSoonState world={world} />
          )}
        </div>
      </section>

      {!embedded && <Footer />}
    </div>
  );
};

export default WorldDetail;
